import { cssColor, num, str } from './settingValue.js';
const STYLES = [
    'none',
    'uniform',
    'bottom',
    'top',
    'top-bottom',
    'vignette',
];
/** The styles that fade over a DISTANCE — the only ones for which `size` means anything. */
const GRADIENT_STYLES = ['bottom', 'top', 'top-bottom', 'vignette'];
/**
 * The inspector fields, spread into a schema's `fields`.
 *
 * Ids are prefixed `mediaOverlay*` and NOT `overlay*`: `field.overlayColor` already answers "Background
 * overlay color" for parallax-hero and vertical-ticker, where the overlay sits on the section's BACKGROUND.
 * Reusing that id would have handed this control the wrong label in seven languages — the kind of defect
 * that is invisible in English, because English reads the schema literal below.
 */
export function mediaOverlayFields(group = 'Overlay') {
    // `equals: [the styles that paint]`, NOT `notEquals: 'none'`. A section that already exists on a live
    // storefront has NO `mediaOverlayStyle` key at all, and `notEquals` holds on an absent value — which
    // opened the inspector on a colour and a strength slider for an overlay nobody had chosen. Listing what
    // must be TRUE keeps an absent value hidden, which is the honest reading of "no overlay".
    const onlyWhenOn = {
        field: 'mediaOverlayStyle',
        equals: STYLES.filter((v) => v !== 'none'),
    };
    return [
        {
            id: 'mediaOverlayStyle',
            type: 'select',
            label: 'Overlay',
            group,
            default: 'none',
            info: 'Lays a tint over the media. A fade is what makes a photo dissolve into the section next to it instead of ending on a hard edge.',
            options: [
                { value: 'none', label: 'None' },
                { value: 'uniform', label: 'Even tint' },
                { value: 'bottom', label: 'Fade from the bottom' },
                { value: 'top', label: 'Fade from the top' },
                { value: 'top-bottom', label: 'Fade from both edges' },
                { value: 'vignette', label: 'Darken the edges' },
            ],
        },
        {
            id: 'mediaOverlayColor',
            type: 'color',
            label: 'Overlay color',
            group,
            default: { ref: 'custom', value: '#000000' },
            info: 'Usually the colour of the band the media sits on, so the fade lands on it invisibly.',
            visibleWhen: onlyWhenOn,
        },
        {
            id: 'mediaOverlayOpacity',
            type: 'range',
            label: 'Overlay strength',
            group,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            // 85, not 60 (18/09, and 80 was still short): a scrim that has to be TURNED UP to be noticed is a
            // setting that reads broken. The curve above does most of the work; this is the rest of it.
            default: 85,
            visibleWhen: onlyWhenOn,
        },
        {
            id: 'mediaOverlaySize',
            type: 'range',
            label: 'Fade distance',
            group,
            min: 10,
            max: 100,
            step: 5,
            unit: '%',
            default: 60,
            info: 'How much of the media the fade covers — 100% reaches the far edge.',
            visibleWhen: { field: 'mediaOverlayStyle', equals: [...GRADIENT_STYLES] },
        },
    ];
}
/**
 * THE PHONE'S OWN OVERLAY (18/09/2026) — three fields, filed under the inspector's "Mobile" part.
 *
 * Three and not four: the COLOUR is deliberately not per-device. A scrim's colour is the colour of the
 * band the media sits on, and that band is the same on both readers — while a control offered per device
 * would have to show SOMETHING while it inherits, and whatever it showed (its own default, or a copy of
 * the desktop's) would be a lie the day the desktop colour changed. What genuinely differs between a
 * 1 900px banner and a 390px panel is the SHAPE of the fade and how hard it lands, so that is what moves.
 *
 * `inherit` is the default on the style, which is what makes these fields free to add to sections that
 * are already live: absent = the phone paints exactly the desktop overlay, as it always has.
 */
export function mobileMediaOverlayFields(group = 'Mobile') {
    const onlyWhenOn = {
        field: 'mobileMediaOverlayStyle',
        equals: STYLES.filter((v) => v !== 'none'),
    };
    return [
        {
            id: 'mobileMediaOverlayStyle',
            type: 'select',
            label: 'Overlay',
            group,
            default: 'inherit',
            info: 'A fade that reads well over a wide desktop banner often swallows a tall phone panel. Leave it on “Same as desktop” to keep one overlay everywhere.',
            options: [
                { value: 'inherit', label: 'Same as desktop' },
                { value: 'none', label: 'None' },
                { value: 'uniform', label: 'Even tint' },
                { value: 'bottom', label: 'Fade from the bottom' },
                { value: 'top', label: 'Fade from the top' },
                { value: 'top-bottom', label: 'Fade from both edges' },
                { value: 'vignette', label: 'Darken the edges' },
            ],
        },
        {
            id: 'mobileMediaOverlayOpacity',
            type: 'range',
            label: 'Overlay strength',
            group,
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'mediaOverlayOpacity',
            visibleWhen: onlyWhenOn,
        },
        {
            id: 'mobileMediaOverlaySize',
            type: 'range',
            label: 'Fade distance',
            group,
            min: 10,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'mediaOverlaySize',
            visibleWhen: { field: 'mobileMediaOverlayStyle', equals: [...GRADIENT_STYLES] },
        },
    ];
}
/** Read the four settings off the untyped document. Never throws; anything odd falls back to the default. */
export function readMediaOverlay(s) {
    const raw = str(s.mediaOverlayStyle, 'none');
    return {
        style: STYLES.includes(raw) ? raw : 'none',
        color: cssColor(s.mediaOverlayColor, '#000000'),
        opacity: clamp(num(s.mediaOverlayOpacity, 85), 0, 100),
        size: clamp(num(s.mediaOverlaySize, 60), 10, 100),
    };
}
/**
 * The PHONE's overlay, or `null` when the phone keeps the desktop's (18/09/2026).
 *
 * A scrim is the setting that travels worst between the two readers: a fade over the bottom 60 % of a
 * 21:9 desktop banner is a whisper, and the same 60 % of a 9:16 phone panel swallows the product. So the
 * phone gets its own — and, like every other per-device setting here, ABSENT MEANS "same as desktop", so
 * no page already published changes.
 *
 * Only the STYLE decides whether the phone overrides at all. Its colour, strength and distance each fall
 * back to the desktop's when they are not set, so a seller who only wants "a fade instead of a tint on
 * the phone" changes ONE control and keeps the palette they already chose.
 */
export function readMobileMediaOverlay(s, desktop) {
    const raw = str(s.mobileMediaOverlayStyle, 'inherit');
    if (raw === 'inherit' || raw === '')
        return null;
    const style = raw;
    return {
        style: STYLES.includes(style) ? style : 'none',
        // The colour is the desktop's, always — see `mobileMediaOverlayFields` for why it is not offered twice.
        color: desktop.color,
        opacity: clamp(num(s.mobileMediaOverlayOpacity, desktop.opacity), 0, 100),
        size: clamp(num(s.mobileMediaOverlaySize, desktop.size), 10, 100),
    };
}
function clamp(v, min, max) {
    return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : min;
}
/** `color` at `pct`% alpha. `color-mix` so a theme token (`var(--aqx-theme-…)`) survives the blend. */
function alpha(color, pct) {
    const p = Math.round(Math.min(100, Math.max(0, pct)) * 10) / 10;
    if (p <= 0)
        return 'transparent';
    return `color-mix(in srgb, ${color} ${p}%, transparent)`;
}
/**
 * The alpha curve of a fade: the fraction of the chosen strength at each fifth of the distance.
 *
 * `(1 − t³)²`, sampled at 0 / .2 / .4 / .6 / .8 / 1. It took THREE shapes to land here, and the two that
 * were wrong were wrong in the same direction, so the reasoning is worth keeping:
 *
 *  1. An ease-out (55 % of the strength a third of the way in). The founder: "pas très visible".
 *  2. The complement of a smoothstep, `(1-t)²(1+2t)` — better at the edge, but still down to HALF the
 *     strength at the midpoint of the fade. The founder again: "they still don't have much impact".
 *
 * The misreading behind both: a scrim is not a transition between two colours, it is a MASS of darkness
 * that happens to end softly. So the curve holds near-full strength through the first two fifths (98 %,
 * 88 %) and spends the whole drop in the last third — 77 % at the midpoint where the smoothstep gave 50 %.
 *
 * What does NOT change: the slope is still ZERO at the far end (`d/dt (1-t³)² = -6t²(1-t³)` → 0 at t=1),
 * so the fade dies into the picture instead of ending on the faint line a straight ramp leaves behind.
 * That property is the whole reason this is a curve and not two stops.
 */
const CURVE = [
    [0, 1],
    [0.2, 0.98],
    [0.4, 0.88],
    [0.6, 0.62],
    [0.8, 0.24],
    [1, 0],
];
/** The colour stops of a fade that travels `size`% from `from` (0%) to nothing (`size`%). */
function curveStops(color, opacity, size, from = 0) {
    return CURVE.map(([at, factor]) => factor <= 0
        ? `transparent ${round(from + size)}%`
        : `${alpha(color, opacity * factor)} ${round(from + size * at)}%`).join(', ');
}
/** A fade that reads as one heavy wash at the edge and dies into the picture, never on a visible line. */
function fade(color, opacity, size, to) {
    return `linear-gradient(to ${to}, ${curveStops(color, opacity, size)})`;
}
function round(v) {
    return Math.round(v * 10) / 10;
}
/**
 * The CSS `background-image` for the overlay layer — or `undefined` when there is nothing to paint
 * (style `none`, or a strength of 0, which is the same thing said with the slider).
 *
 * Always a `background-image` (even the flat tint is a two-stop gradient) so the caller has exactly ONE
 * property to set, whatever the style.
 */
export function mediaOverlayBackground(c) {
    if (c.style === 'none' || c.opacity <= 0)
        return undefined;
    const full = alpha(c.color, c.opacity);
    switch (c.style) {
        case 'uniform':
            return `linear-gradient(${full}, ${full})`;
        case 'bottom':
            // "from the bottom" = darkest at the bottom edge, fading UPWARD.
            return fade(c.color, c.opacity, c.size, 'top');
        case 'top':
            return fade(c.color, c.opacity, c.size, 'bottom');
        case 'top-bottom':
            return `${fade(c.color, c.opacity, c.size / 2, 'top')}, ${fade(c.color, c.opacity, c.size / 2, 'bottom')}`;
        case 'vignette': {
            // Same curve, read from the CENTRE outward: transparent where the darkening starts, heaviest at the
            // rim. `size` reads the same way as elsewhere — how far in from the edge the darkening reaches.
            const stops = [...CURVE]
                .reverse()
                .map(([at, factor]) => factor <= 0
                ? `transparent ${round(100 - c.size)}%`
                : `${alpha(c.color, c.opacity * factor)} ${round(100 - c.size * at)}%`)
                .join(', ');
            return `radial-gradient(ellipse at center, ${stops})`;
        }
        default:
            return undefined;
    }
}
//# sourceMappingURL=mediaOverlay.js.map